/**
 * Staged requirement changes (Flint draft-and-approve): Inky proposes additions, updates and
 * removals for a project's requirements; nothing is written until the user approves the card.
 *
 * `stageRequirementChanges` validates a proposal against the project the browser knows
 * (in-browser loop; the server agent has its own validator with the same rules) and returns
 * either self-correcting errors or the card. `commitRequirementChanges` applies the approved
 * rows through the app's own AppContext actions (so RLS, ownership rules and local state stay
 * consistent) and reports every row's outcome.
 */

/** Priorities the app uses. */
export const PRIORITIES = ["Critical", "High", "Medium", "Low"];

/**
 * Stable short citation ref for a row id (`R-` requirement, `S-` session, `F-` file).
 * @param {'R'|'S'|'F'} kind - Ref kind.
 * @param {string} id - Row uuid.
 * @returns {string} Ref like R-1a2b3c.
 */
export const refFor = (kind, id) => `${kind}-${String(id || "").replace(/[^0-9a-f]/gi, "").slice(0, 6).toLowerCase()}`;

/**
 * Canonical ref ("r-1A2B3C" -> "R-1a2b3c") or null.
 * @param {*} value - Raw ref.
 * @returns {string|null} Ref.
 */
export const normalizeRef = (value) => {
  const match = /^([RSF])-([0-9a-f]{6})$/i.exec(String(value || "").trim());
  return match ? `${match[1].toUpperCase()}-${match[2].toLowerCase()}` : null;
};

/**
 * One line of text, cut.
 * @param {*} value - Raw value.
 * @param {number} max - Max characters.
 * @returns {string} Text.
 */
const line = (value, max) => {
  const flat = String(value ?? "").replace(/\s+/g, " ").trim();
  return flat.length > max ? flat.slice(0, max) : flat;
};

/**
 * Validate a proposal and build the review card.
 * @param {object} params - Inputs.
 * @param {object} params.project - AppContext project (id, name, requirements, sessions).
 * @param {Array<object>} params.changes - Proposed changes ({action, requirementId?, title?, description?, type?, priority?, sessionId?, acceptanceCriteria?, sourceRef?, sourceTimestamp?, sourceQuote?}).
 * @param {string} [params.summary] - One-line summary.
 * @returns {{ok: true, card: object, counts: object}|{ok: false, errors: string[]}} Result.
 */
export const stageRequirementChanges = ({ project, changes, summary = "" }) => {
  const reqs = project?.requirements || [];
  const sessions = project?.sessions || [];
  const list = Array.isArray(changes) ? changes : [];
  if (!list.length) return { ok: false, errors: ["changes is empty."] };
  if (list.length > 50) return { ok: false, errors: ["Stage at most 50 changes at once."] };
  const findReq = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    const ref = normalizeRef(raw);
    return reqs.find((r) => r.id === raw || (ref && refFor("R", r.id) === ref)) || null;
  };
  const findSession = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    const ref = normalizeRef(raw);
    return sessions.find((s) => s.id === raw || (ref && refFor("S", s.id) === ref)) || null;
  };
  const titles = new Set(reqs.map((r) => String(r.title || "").trim().toLowerCase()));
  const errors = [];
  const staged = [];

  list.forEach((raw, index) => {
    const c = raw && typeof raw === "object" ? raw : {};
    const where = `Change ${index + 1} (${c.action || "missing action"})`;
    if (!["add", "update", "delete"].includes(c.action)) {
      errors.push(`${where}: action must be add, update or delete.`);
      return;
    }
    let priority = null;
    if (c.priority) {
      priority = PRIORITIES.find((p) => p.toLowerCase() === String(c.priority).toLowerCase()) || null;
      if (!priority) {
        errors.push(`${where}: priority must be one of ${PRIORITIES.join(", ")}.`);
        return;
      }
    }
    const sourceRef = String(c.sourceRef || "").trim();
    const normalized = normalizeRef(sourceRef);
    const source = sourceRef.toLowerCase() === "user"
      ? { kind: "user", ref: "user" }
      : normalized
        ? { kind: { R: "requirement", S: "session", F: "file" }[normalized[0]], ref: normalized }
        : null;
    if (c.action !== "delete" && !source) {
      errors.push(`${where}: sourceRef is required: an R-, S- or F- ref from a tool result, or "user" (with sourceQuote).`);
      return;
    }
    if (source?.kind === "user" && !String(c.sourceQuote || "").trim()) {
      errors.push(`${where}: a "user" source needs sourceQuote with the user's own words.`);
      return;
    }
    const session = c.sessionId ? findSession(c.sessionId) : source?.kind === "session" ? findSession(source.ref) : null;
    if (c.sessionId && !session) {
      errors.push(`${where}: meeting ${c.sessionId} is not in ${project.name}.`);
      return;
    }
    const base = {
      key: `c${index + 1}`,
      action: c.action,
      ...(source ? { source: { ...source, ...(c.sourceTimestamp ? { timestamp: line(c.sourceTimestamp, 20) } : {}), ...(c.sourceQuote ? { quote: line(c.sourceQuote, 300) } : {}) } } : {}),
      ...(session ? { sessionId: session.id, sessionName: session.name } : {}),
    };
    const criteria = Array.isArray(c.acceptanceCriteria) ? c.acceptanceCriteria.map((a) => line(a, 300)).filter(Boolean).slice(0, 12) : null;
    if (c.action === "add") {
      const title = line(c.title, 200);
      const description = String(c.description || "").trim().slice(0, 4000);
      if (!title || !description) {
        errors.push(`${where}: add needs title and description.`);
        return;
      }
      if (titles.has(title.toLowerCase())) {
        const existing = reqs.find((r) => String(r.title || "").trim().toLowerCase() === title.toLowerCase());
        errors.push(`${where}: "${title}" already exists${existing ? ` as ${refFor("R", existing.id)}` : " in this batch"}. Use update instead.`);
        return;
      }
      titles.add(title.toLowerCase());
      staged.push({ ...base, title, description, type: line(c.type, 60) || "Functional", priority: priority || "Medium", acceptanceCriteria: criteria || [] });
      return;
    }
    const req = findReq(c.requirementId);
    if (!req) {
      errors.push(`${where}: requirement ${c.requirementId || "(none)"} is not in ${project.name}.`);
      return;
    }
    const before = { title: req.title, description: line(req.description, 600), type: req.type || null, priority: req.priority || null };
    if (c.action === "delete") {
      staged.push({ ...base, requirementId: req.id, ref: refFor("R", req.id), title: req.title, before });
      return;
    }
    staged.push({
      ...base,
      requirementId: req.id,
      ref: refFor("R", req.id),
      title: c.title ? line(c.title, 200) : req.title,
      description: c.description ? String(c.description).trim().slice(0, 4000) : req.description || "",
      type: c.type ? line(c.type, 60) : req.type || null,
      priority: priority || req.priority || null,
      ...(criteria ? { acceptanceCriteria: criteria } : {}),
      before,
    });
  });

  if (errors.length) return { ok: false, errors };
  const counts = { add: 0, update: 0, delete: 0 };
  staged.forEach((change) => {
    counts[change.action] += 1;
  });
  return {
    ok: true,
    counts,
    card: {
      kind: "requirement_changes",
      tool: "requirements.stage",
      status: "pending",
      projectId: project.id,
      projectName: project.name,
      summary: line(summary, 300),
      changes: staged,
    },
  };
};

/**
 * Apply the approved rows of a requirement_changes card through AppContext actions.
 * @param {object} params - Inputs.
 * @param {object} params.card - The card (projectId, changes).
 * @param {{changes?: Array<object>, excluded?: string[]}} params.values - The user's edits and the keys they left out.
 * @param {object} params.actions - AppContext actions (addRequirement, updateRequirement, deleteRequirement).
 * @returns {Promise<{ok: boolean, summary: string, outcome: string, results: Array<object>}>} Outcome.
 */
export const commitRequirementChanges = async ({ card, values = {}, actions }) => {
  const edited = new Map((values.changes || []).map((change) => [change.key, change]));
  const excluded = new Set(values.excluded || []);
  const rows = (card.changes || []).filter((change) => !excluded.has(change.key)).map((change) => ({ ...change, ...(edited.get(change.key) || {}) }));
  if (!rows.length) throw new Error("Select at least one change to approve.");
  const results = [];
  for (const change of rows) {
    let result;
    try {
      if (change.action === "add") {
        result = await actions.addRequirement({
          projectId: card.projectId,
          sessionId: change.sessionId || null,
          title: change.title,
          description: change.description,
          type: change.type || "Functional",
          priority: change.priority || "Medium",
          acceptanceCriteria: change.acceptanceCriteria || [],
        });
      } else if (change.action === "update") {
        result = await actions.updateRequirement(card.projectId, change.requirementId, {
          title: change.title,
          description: change.description,
          type: change.type,
          priority: change.priority,
          ...(Array.isArray(change.acceptanceCriteria) ? { acceptanceCriteria: change.acceptanceCriteria } : {}),
        });
      } else {
        result = await actions.deleteRequirement(card.projectId, change.requirementId);
      }
    } catch (error) {
      result = { success: false, error: error?.message || String(error) };
    }
    results.push({ key: change.key, action: change.action, title: change.title, ok: Boolean(result?.success), error: result?.success ? undefined : result?.error || "Failed" });
  }
  const done = results.filter((r) => r.ok);
  const failed = results.filter((r) => !r.ok);
  const verb = { add: "added", update: "updated", delete: "removed" };
  const parts = ["add", "update", "delete"]
    .map((action) => {
      const n = done.filter((r) => r.action === action).length;
      return n ? `${n} ${verb[action]}` : null;
    })
    .filter(Boolean);
  const outcome = `${parts.length ? parts.join(", ") : "Nothing applied"}${failed.length ? `; ${failed.length} failed (${failed[0].error})` : ""}.`;
  if (!done.length) throw new Error(`None of the changes could be applied: ${failed[0]?.error || "unknown error"}.`);
  return { ok: true, summary: outcome, outcome, results };
};
